import { useState, useMemo, useEffect, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

/* ─── Shared helpers ─── */
const safeNum = (v: unknown) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return isNaN(n) ? 0 : n;
};
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, AreaChart, Area,
} from 'recharts';
import { useAuth } from '@/hooks/useAuth';
import { hasPhpBackend, hasAuthToken, apiSaveAssessment, apiMyAssessments } from '@/lib/phpApi';
import {
  MapPin, Save, Check, Database, LayoutDashboard, Cog, Droplets, ShieldCheck, Filter,
  TrendingUp, FileText, Printer, AlertTriangle, ChevronRight, Zap, Award,
  ArrowLeft, Trash2, Download, X, Beaker, Activity, BarChart3
} from 'lucide-react';
import type { BlendedParameters, AssessmentResult, DischargeLimits, SavedSample, WaterParameters, DosingConfig } from '../types/calculator';
import type { StandardCode } from '../types/standards';
import { getStandard } from '../types/standards';
import { generateProcessTrainReportPDF } from '@/lib/pdfGenerator';
import ProcessTrainOverview from './ProcessTrainOverview';
import AssessmentResults from './AssessmentResults';
import ProcessFlow from './ProcessFlow';
import ProcessTrainDetail from './ProcessTrainDetail';
import SalinitySection from './SalinitySection';
import FiltrationSection from './FiltrationSection';
import ProcessRecommendations from './ProcessRecommendations';
import BlendedSummary from './BlendedSummary';
import SampleReportPrint from './SampleReportPrint';
import ProcessTrainDosing from './ProcessTrainDosing';
import LamellaSettlement from './LamellaSettlement';
import CheckDataPopup from './CheckDataPopup';

interface Props {
  result: AssessmentResult;
  blended: BlendedParameters;
  limits: DischargeLimits;
  samples: SavedSample[];
  hasSamplesChart: boolean;
  params: WaterParameters;
  dosingConfig: DosingConfig[];
  activeStandard: StandardCode;
}

/* ─── Dark Tooltip for Recharts ─── */
function DarkTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-slate-600 bg-slate-900/95 backdrop-blur-sm px-3 py-2 shadow-xl">
      <p className="text-xs font-bold text-slate-300 mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} className="text-xs flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-400">{p.name}:</span>
          <span className="text-white font-mono font-bold">{typeof p.value === 'number' ? p.value.toLocaleString('en-GB', { maximumFractionDigits: 1 }) : p.value}</span>
        </p>
      ))}
    </div>
  );
}

export default memo(ResultsTabs);

function ResultsTabs({ result, blended, limits, samples, hasSamplesChart, params, dosingConfig, activeStandard }: Props) {
  const { isAuthenticated } = useAuth();
  const canSaveToServer = isAuthenticated && hasAuthToken();
  const canSaveLocally = isAuthenticated && !hasAuthToken();
  // const saveMutation = trpc.results.save.useMutation();
  const [saved, setSaved] = useState(false);
  const [savedReportNumber, setSavedReportNumber] = useState("");
  const [saveError, setSaveError] = useState("");
  const [locStatus, setLocStatus] = useState<"idle" | "fetching" | "done" | "error">("idle");
  const standard = getStandard(activeStandard);

  const tabs = useMemo(() => [
    { key: 'overview', label: 'Overview', icon: LayoutDashboard, color: '#22d3ee' },
    { key: 'process', label: 'Process Train', icon: Cog, color: 'var(--rb-brand)' },
    { key: 'dosing', label: 'Dosing', icon: Beaker, color: '#f472b6' },
    { key: 'assessment', label: 'Assessment', icon: ShieldCheck, color: '#34d399' },
    { key: 'salinity', label: 'Salinity', icon: Droplets, color: '#38bdf8' },
    { key: 'filtration', label: 'Filtration', icon: Filter, color: '#a78bfa' },
    ...(hasSamplesChart ? [{ key: 'samples', label: 'Trends', icon: TrendingUp, color: '#fb923c' }] : []),
    ...(canSaveToServer ? [{ key: 'myresults', label: 'My Results', icon: Database, color: '#f472b6' }] : []),
    ...(canSaveToServer ? [{ key: 'sampleReports', label: 'Reports', icon: FileText, color: 'var(--rb-text-2)' }] : []),
    { key: 'print', label: 'Print', icon: Printer, color: 'var(--rb-text-3)' },
  ] as const, [hasSamplesChart, canSaveToServer]);

  const [activeTab, setActiveTab] = useState<string>('overview');
  const warning = result.dosingWarning;

  /* KPI data for overview header */
  const overviewKpis = useMemo(() => [
    { label: 'Treatability Index', value: safeNum(result?.overallTreatabilityIndex).toFixed(2), icon: Activity, color: '#22d3ee' },
    { label: 'Classification', value: result?.treatabilityClassification ?? '—', icon: Award, color: 'var(--rb-brand)' },
    { label: 'Biological Viability', value: `${(result?.biologicalViability ?? []).filter(v => v.passFail === 'PASS').length}/${(result?.biologicalViability ?? []).length}`, icon: ShieldCheck, color: '#34d399' },
    { label: 'Process Stages', value: result?.simulation?.stages?.length ?? 0, icon: Cog, color: '#a78bfa' },
    { label: 'Toxic Params', value: safeNum(result?.toxicParametersCount), icon: AlertTriangle, color: safeNum(result?.toxicParametersCount) > 0 ? '#f43f5e' : 'var(--rb-text-3)' },
    { label: 'pH after 1st', value: result?.simulation?.stages?.[0]?.ph?.toFixed(2) ?? 'N/A', icon: Beaker, color: '#38bdf8' },
  ], [result]);

  const handleSaveToServer = () => {
    setSaveError(""); setSaved(false); setLocStatus("fetching");

    // ─── Auth check ─────────────────────────────────────────
    // Priority 1: No PHP backend at all → save locally only
    if (!hasPhpBackend()) {
      setLocStatus("idle"); setSaved(true);
      toast.success("Result saved locally (server offline)");
      return;
    }

    // Priority 2: PHP backend exists but user has no token
    // Fall back to local save instead of showing an error
    const token = localStorage.getItem("rb_api_token");
    if (!token) {
      setLocStatus("idle");
      setSaved(true);
      toast.success("Result saved locally (sign in to save to server)");
      setTimeout(() => { setSaved(false); }, 3000);
      return;
    }

    console.log("[Save] Token found, length:", token.length, "— proceeding with server save");
    const doSave = (geo?: { latitude?: number; longitude?: number; city?: string; country?: string }) => {
      apiSaveAssessment({
        sampleName: `Assessment ${new Date().toLocaleDateString('en-GB')}`,
        params: params as unknown as Record<string, unknown>,
        blended: blended as unknown as Record<string, unknown>,
        limits: limits as unknown as Record<string, unknown>,
        result: result as unknown as Record<string, unknown>,
        dosingConfig: dosingConfig as unknown as Record<string, unknown>[],
        equipmentValidation: (result?.equipmentValidation ?? []) as unknown as Record<string, unknown>[],
        simulation: result?.simulation as unknown as Record<string, unknown>,
        standard: activeStandard,
        latitude: geo?.latitude,
        longitude: geo?.longitude,
        city: geo?.city,
        country: geo?.country,
      }).then((res) => {
        console.log("[Save] Server response:", res);
        setSaved(true);
        setSavedReportNumber(res.reportNumber ?? String(res.id ?? ""));
        setLocStatus("done");
        toast.success(`Saved to server — Report ${res.reportNumber ?? res.id}`);
        setTimeout(() => { setSaved(false); setSavedReportNumber(""); }, 3000);
      }).catch((err) => {
        console.error("[Save] Server error:", err);
        const msg = err instanceof Error ? err.message : String(err);
        // Give specific guidance for auth errors
        if (msg.includes("401") || msg.includes("Authentication") || msg.includes("token") || msg.includes("auth")) {
          setSaveError("Server rejected the auth token. The token may have expired. Log out and log back in to get a fresh token.");
          toast.error("Auth failed — please log in again");
        } else {
          setSaveError(msg || "Save failed");
          toast.error(`Save failed: ${msg || "Server error"}`);
        }
        setLocStatus("idle");
      });
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => doSave({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
        () => doSave(),
        { timeout: 5000 }
      );
    } else {
      doSave();
    }
  };

  return (
    <div className="space-y-4">
      {/* ═════ OVERVIEW KPI ROW ═════ */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {overviewKpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="rb-card p-3.5 border border-slate-800/50 hover:border-slate-600 transition-all"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${kpi.color}15` }}>
                  <Icon className="w-3.5 h-3.5" style={{ color: kpi.color }} />
                </div>
                <span className="text-[10px] text-slate-500 uppercase font-bold">{kpi.label}</span>
              </div>
              <p className="text-lg font-bold text-white">{kpi.value}</p>
            </motion.div>
          );
        })}
      </div>

      {/* ═════ STANDARD BADGE + SAVE ═════ */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">Standard</span>
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white border"
            style={{
              background: standard.code === 'UK_EA' ? 'rgba(30,58,95,0.4)' : standard.code === 'AU_EPA' ? 'rgba(5,150,105,0.3)' : 'rgba(148,163,184,0.2)',
              borderColor: standard.code === 'UK_EA' ? 'var(--rb-active)' : standard.code === 'AU_EPA' ? '#059669' : 'var(--rb-text-2)',
            }}
          >
            <span>{standard.flag}</span> {standard.shortName}
          </span>
          <span className="text-xs text-slate-500">{standard.regulatoryBody}</span>
        </div>
        <div className="flex items-center gap-2">
          {saveError && <span className="text-xs text-red-400 bg-red-500/10 px-2 py-1 rounded-lg border border-red-500/20">{saveError}</span>}
          {saved && (
            <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-lg border border-emerald-500/20 flex items-center gap-1">
              <Check className="w-3 h-3" /> Saved
              {savedReportNumber && <span className="font-mono text-[10px] text-slate-400">[{savedReportNumber}]</span>}
            </span>
          )}
          {canSaveToServer && (
            <button
              onClick={handleSaveToServer}
              disabled={locStatus === "fetching"}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-rb-brand text-slate-200 hover:bg-[#b8931f] disabled:opacity-40 transition-all shadow-md"
            >
              <Save className="w-3.5 h-3.5" />
              {locStatus === "fetching" ? "Saving..." : "Save to Server"}
            </button>
          )}
          {canSaveLocally && (
            <button
              onClick={handleSaveToServer}
              disabled={locStatus === "fetching"}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-slate-700 text-slate-200 hover:bg-slate-600 disabled:opacity-40 transition-all shadow-md"
            >
              <Save className="w-3.5 h-3.5" />
              {locStatus === "fetching" ? "Saving..." : "Save Locally"}
            </button>
          )}
        </div>
      </div>

      {/* ═════ DARK TAB BAR ═════ */}
      <div className="flex flex-wrap gap-1.5 p-1.5 rounded-xl bg-slate-900/50 border border-slate-800/60">
        {tabs.map(tab => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative flex items-center gap-2 px-3.5 py-2.5 rounded-lg text-xs font-bold transition-all hover:shadow-md hover:shadow-cyan-500/5 ${
                isActive
                  ? 'text-white shadow-lg'
                  : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
              }`}
              style={isActive ? { background: `${tab.color}20`, border: `1px solid ${tab.color}40` } : { border: '1px solid transparent' }}
            >
              <TabIcon className="w-3.5 h-3.5" style={{ color: isActive ? tab.color : undefined }} />
              <span className="hidden sm:inline">{tab.label}</span>
              {isActive && (
                <motion.div
                  layoutId="resultsTabIndicator"
                  className="absolute inset-0 rounded-lg -z-10"
                  style={{ background: `${tab.color}12`, border: `1px solid ${tab.color}30` }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* ═════ WARNING BANNER ═════ */}
      <AnimatePresence>
        {warning?.show && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            className={`rounded-xl border p-4 ${
              warning.severity === 'critical'
                ? 'border-red-500/30 bg-red-500/5'
                : 'border-amber-500/20 bg-amber-500/5'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                warning.severity === 'critical' ? 'bg-red-500/15' : 'bg-amber-500/15'
              }`}>
                <AlertTriangle className={`w-5 h-5 ${warning.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-bold mb-1 ${warning.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                  {warning.severity === 'critical' ? 'Critical: Dosing Required' : 'Dosing Warning'}
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">{warning.message}</p>
                {warning.affectedStages.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {warning.affectedStages.map((stage, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-bold text-slate-300">
                        {stage}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-2 text-[10px] text-slate-500 bg-slate-800/50 rounded-lg p-2 border border-slate-800/50">
                  <strong className="text-rb-brand">Note:</strong> Coagulation & flocculation dosing is required for Lamella Clarifiers. DAF also needs coagulant + polyelectrolyte.
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═════ TAB CONTENT ═════ */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="space-y-5"
        >
          {/* ── Overview ── */}
          {activeTab === 'overview' && (
            <div className="space-y-5">
              {blended.source !== 'base' && (
                <div className="rb-card p-5">
                  <BlendedSummary blended={blended} />
                </div>
              )}
              <div className="rb-card p-5">
                <ProcessTrainOverview simulation={result.simulation} limits={limits} />
              </div>
              <div className="rb-card p-5">
                <ProcessRecommendations result={result} />
              </div>
            </div>
          )}

          {/* ── Process Train ── */}
          {activeTab === 'process' && (
            <div className="space-y-5">
              {result.simulation?.lamellaSettlement && (
                <LamellaSettlement settlement={result.simulation.lamellaSettlement} />
              )}
              <div className="rb-card p-5">
                <ProcessFlow simulation={result.simulation} limits={limits} />
              </div>
              <div className="rb-card p-5">
                <ProcessTrainDetail result={result} />
              </div>
            </div>
          )}

          {/* ── Dosing ── */}
          {activeTab === 'dosing' && (
            <div className="space-y-5">
              <ProcessTrainDosing result={result} />
            </div>
          )}

          {/* ── Assessment ── */}
          {activeTab === 'assessment' && (
            <div className="rb-card p-5">
              <AssessmentResults result={result} />
            </div>
          )}

          {/* ── Salinity ── */}
          {activeTab === 'salinity' && (
            <div className="rb-card p-5">
              <SalinitySection result={result} />
            </div>
          )}

          {/* ── Filtration ── */}
          {activeTab === 'filtration' && (
            <div className="rb-card p-5">
              <FiltrationSection result={result} />
            </div>
          )}

          {/* ── Sample Trends ── */}
          {activeTab === 'samples' && hasSamplesChart && (
            <SampleTrendsTabDark samples={samples} limits={limits} />
          )}

          {/* ── My Results ── */}
          {activeTab === 'myresults' && isAuthenticated && (
            <MyResultsTabDark />
          )}

          {/* ── Sample Reports ── */}
          {activeTab === 'sampleReports' && isAuthenticated && (
            <SampleReportsTabDark />
          )}

          {/* ── Print ── */}
          {activeTab === 'print' && (
            <PrintReportTabDark
              result={result} blended={blended} limits={limits}
              samples={samples} hasSamplesChart={hasSamplesChart}
              reportNumber={savedReportNumber}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════════════════════════
   DARK SAMPLE TRENDS TAB
   ═══════════════════════════════════════════ */
function SampleTrendsTabDark({ samples, limits }: { samples: SavedSample[]; limits: DischargeLimits }) {
  const sorted = [...samples].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const chartData = sorted.map(s => ({
    date: new Date(s.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
    fullDate: new Date(s.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }),
    name: s.name,
    BOD: safeNum(s.params?.bod5),
    COD: safeNum(s.params?.cod),
    TSS: safeNum(s.params?.tss),
    pH: safeNum(s.params?.ph),
    TDS: safeNum(s.params?.tds),
    TKN: safeNum(s.params?.tkn),
    TP: safeNum(s.params?.totalPhosphorus),
  }));

  const len = chartData.length || 1;
  const avgBOD = chartData.reduce((s, d) => s + d.BOD, 0) / len;
  const avgCOD = chartData.reduce((s, d) => s + d.COD, 0) / len;
  const maxBOD = Math.max(...chartData.map(d => d.BOD));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-cyan-400" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white">Sample Trend Analysis</h3>
          <p className="text-xs text-slate-500">{samples.length} samples tracked over time</p>
        </div>
        <div className="ml-auto flex gap-3">
          <div className="text-right">
            <p className="text-xs text-slate-500">Avg BOD</p>
            <p className="text-sm font-bold text-cyan-400">{avgBOD.toFixed(0)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Avg COD</p>
            <p className="text-sm font-bold text-rb-brand">{avgCOD.toFixed(0)}</p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rb-card p-4">
          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-center">BOD, COD & TSS Over Time</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="bodGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3}/><stop offset="95%" stopColor="#22d3ee" stopOpacity={0}/></linearGradient>
                  <linearGradient id="codGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="var(--rb-brand)" stopOpacity={0.3}/><stop offset="95%" stopColor="var(--rb-brand)" stopOpacity={0}/></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <YAxis tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <ReTooltip content={<DarkTooltip />} />
                <Legend wrapperStyle={{ fontSize: 'var(--rb-font-xs-)', color: 'var(--rb-text-2)' }} />
                <Area type="monotone" dataKey="BOD" stroke="#22d3ee" fill="url(#bodGrad)" strokeWidth={2} />
                <Area type="monotone" dataKey="COD" stroke="var(--rb-brand)" fill="url(#codGrad)" strokeWidth={2} />
                <Line type="monotone" dataKey="TSS" stroke="#64748b" strokeWidth={2} dot={{ r: 3, fill: 'var(--rb-text-3)' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rb-card p-4">
          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-center">pH & Nutrients Over Time</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <YAxis yAxisId="left" tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <ReTooltip content={<DarkTooltip />} />
                <Legend wrapperStyle={{ fontSize: 'var(--rb-font-xs-)', color: 'var(--rb-text-2)' }} />
                <Bar yAxisId="left" dataKey="pH" fill="var(--rb-brand)" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="TKN" fill="var(--rb-active)" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="TP" fill="#64748b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="rb-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-800/60 border-b border-slate-700">
                {['Sample', 'Date', 'BOD', 'COD', 'TSS', 'pH', 'TDS', 'TKN', 'TP'].map(h => (
                  <th key={h} className="py-2.5 px-3 font-bold text-slate-400 uppercase text-[10px] tracking-wider text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.map((row, i) => (
                <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                  <td className="py-2.5 px-3 font-semibold text-slate-200">{row.name}</td>
                  <td className="py-2.5 px-3 text-slate-500">{row.fullDate}</td>
                  <td className="py-2.5 px-3 text-right font-mono font-bold text-cyan-400">{row.BOD.toFixed(0)}</td>
                  <td className="py-2.5 px-3 text-right font-mono font-bold text-rb-brand">{row.COD.toFixed(0)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-400">{row.TSS.toFixed(0)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-300">{row.pH.toFixed(2)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-400">{row.TDS.toFixed(0)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-400">{row.TKN.toFixed(0)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-400">{row.TP.toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Compliance mini-charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'BOD vs Limit', data: chartData.map(d => ({ name: d.date, value: d.BOD, limit: limits.bod5 })), color: '#22d3ee' },
          { label: 'COD vs Limit', data: chartData.map(d => ({ name: d.date, value: d.COD, limit: limits.cod })), color: 'var(--rb-brand)' },
          { label: 'TSS vs Limit', data: chartData.map(d => ({ name: d.date, value: d.TSS, limit: limits.tss })), color: '#a78bfa' },
        ].map((chart, ci) => (
          <div key={ci} className="rb-card p-4">
            <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">{chart.label}</h4>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chart.data}>
                  <defs>
                    <linearGradient id={`comp${ci}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chart.color} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={chart.color} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" tick={{ fill: 'var(--rb-text-3)', fontSize: 9 }} stroke="#334155" />
                  <YAxis tick={{ fill: 'var(--rb-text-3)', fontSize: 9 }} stroke="#334155" />
                  <ReTooltip content={<DarkTooltip />} />
                  <Area type="monotone" dataKey="value" stroke={chart.color} fill={`url(#comp${ci})`} strokeWidth={2} />
                  <Line type="monotone" dataKey="limit" stroke="#f43f5e" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   DARK MY RESULTS TAB
   ═══════════════════════════════════════════ */
function MyResultsTabDark() {
  const [myResults, setMyResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!hasPhpBackend()) return;
    setIsLoading(true);
    apiMyAssessments()
      .then((data) => {
        const rows = data.results || [];
        setMyResults(rows);
      })
      .catch(() => setMyResults([]))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <div className="rb-card p-12 text-center">
        <div className="w-8 h-8 border-2 border-slate-600 border-t-[#cda42e] rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-slate-400">Loading your results...</p>
      </div>
    );
  }

  if (myResults.length === 0) {
    return (
      <div className="rb-card p-12 text-center">
        <Database className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <p className="text-sm text-slate-400">No saved results yet.</p>
        <p className="text-xs text-slate-600 mt-1">Run an assessment and click Save to store it here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-rb-brand/10 border border-rb-brand/20 flex items-center justify-center">
          <Database className="w-5 h-5 text-rb-brand" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white">My Saved Results</h3>
          <p className="text-xs text-slate-500">{myResults.length} result{myResults.length !== 1 ? 's' : ''} stored on server</p>
        </div>
      </div>

      <div className="space-y-3">
        {myResults.map((r) => (
          <div key={r.id} className="rb-card p-4 border border-slate-800/50 hover:border-slate-600 transition-all">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-white">{r.sampleName}</span>
                {r.reportNumber && (
                  <span className="text-[10px] font-mono bg-slate-800 text-slate-400 px-2 py-0.5 rounded border border-slate-700">{r.reportNumber}</span>
                )}
                <span className="text-xs text-slate-500">{r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</span>
              </div>
              {(r.latitude || r.city) && (
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />{r.city || `${r.latitude?.toFixed(3)}, ${r.longitude?.toFixed(3)}`}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              {(() => {
                const p = r.params as Record<string, unknown> | null;
                if (!p || typeof p !== 'object') return null;
                return (
                  <>
                    {'flowRate' in p && (
                      <div className="p-2 rounded-lg bg-slate-800/50 border border-slate-800/50">
                        <span className="text-slate-500 block text-[10px] uppercase">Flow</span>
                        <span className="font-medium text-white">{String(p.flowRate)} m³/d</span>
                      </div>
                    )}
                    {'bod' in p && (
                      <div className="p-2 rounded-lg bg-slate-800/50 border border-slate-800/50">
                        <span className="text-slate-500 block text-[10px] uppercase">BOD</span>
                        <span className="font-medium text-white">{String(p.bod)} mg/L</span>
                      </div>
                    )}
                    {'cod' in p && (
                      <div className="p-2 rounded-lg bg-slate-800/50 border border-slate-800/50">
                        <span className="text-slate-500 block text-[10px] uppercase">COD</span>
                        <span className="font-medium text-white">{String(p.cod)} mg/L</span>
                      </div>
                    )}
                    {'tss' in p && (
                      <div className="p-2 rounded-lg bg-slate-800/50 border border-slate-800/50">
                        <span className="text-slate-500 block text-[10px] uppercase">TSS</span>
                        <span className="font-medium text-white">{String(p.tss)} mg/L</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   DARK SAMPLE REPORTS TAB
   ═══════════════════════════════════════════ */
function SampleReportsTabDark() {
  const [myReports, setMyReports] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewingReport, setViewingReport] = useState<any>(null);

  const loadReports = async () => {
    if (!hasPhpBackend()) return;
    setIsLoading(true);
    try {
      const rows = await apiListReports();
      setMyReports(rows || []);
    } catch {
      setMyReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const handleDeleteReport = async (id: number) => {
    if (!hasPhpBackend()) return;
    try {
      await apiDeleteReport(id);
      toast.success('Report deleted');
      loadReports();
    } catch (err: any) {
      toast.error(err.message || 'Delete failed');
    }
  };

  if (isLoading) {
    return (
      <div className="rb-card p-12 text-center">
        <div className="w-8 h-8 border-2 border-slate-600 border-t-[#cda42e] rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-slate-400">Loading reports...</p>
      </div>
    );
  }

  if (myReports.length === 0) {
    return (
      <div className="rb-card p-12 text-center">
        <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <p className="text-sm text-slate-400">No sample reports yet.</p>
        <p className="text-xs text-slate-600 mt-1">Save 3+ water samples to generate a trend report.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {viewingReport ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setViewingReport(null)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 transition-all"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Reports
            </button>
            {/* PDF download is handled by the button inside SampleReportPrint */}
          </div>
          <SampleReportPrint
            reportNumber={viewingReport.reportNumber || `RB-SMP-${new Date(viewingReport.createdAt).getFullYear()}-0000`}
            reportTitle={viewingReport.reportTitle || 'Sample Trend Report'}
            samples={(viewingReport.samples as SavedSample[]) || []}
            createdAt={viewingReport.createdAt}
          />
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-rb-brand/10 border border-rb-brand/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-rb-brand" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">My Sample Reports</h3>
              <p className="text-xs text-slate-500">{myReports.length} report{myReports.length !== 1 ? 's' : ''} stored on server</p>
            </div>
          </div>

          <div className="space-y-3">
            {myReports.map((r) => (
              <div key={r.id} className="rb-card p-4 border border-slate-800/50 hover:border-slate-600 transition-all">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-white">{r.reportTitle || 'Sample Report'}</span>
                    {r.reportNumber && (
                      <span className="text-[10px] font-mono bg-slate-800 text-slate-400 px-2 py-0.5 rounded border border-slate-700">{r.reportNumber}</span>
                    )}
                    <span className="text-xs text-slate-500">{r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{(r.samples as SavedSample[] | undefined)?.length || 0} samples</span>
                    <button
                      onClick={() => { if (hasPhpBackend() && confirm('Delete this sample report?')) handleDeleteReport(r.id); }}
                      disabled={!hasPhpBackend()}
                      className="text-slate-500 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-500/10 disabled:opacity-30"
                      title="Delete report"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer hover:text-rb-brand transition-colors" onClick={() => setViewingReport(r)}>
                  <ChevronRight className="w-3.5 h-3.5" />
                  Click to view and print this report
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   DARK PRINT REPORT TAB
   ═══════════════════════════════════════════ */
function PrintReportTabDark({
  result, blended, limits, samples, hasSamplesChart, reportNumber,
}: {
  result: AssessmentResult;
  blended: BlendedParameters;
  limits: DischargeLimits;
  samples: SavedSample[];
  hasSamplesChart: boolean;
  reportNumber?: string;
}) {
  const { user } = useAuth();
  const sim = result.simulation;
  const sortedSamples = [...samples].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const handlePrint = () => {
    generateProcessTrainReportPDF(result, blended, limits, samples, {
      reportNumber: clientReportNum,
      preparedBy: authorName !== 'Not specified' ? authorName : undefined,
    });
  };

  const clientReportNum = useMemo(() => {
    if (reportNumber) return reportNumber;
    const now = new Date();
    return `RB-TRT-${now.toISOString().slice(0, 10).replace(/-/g, '')}-${now.toTimeString().slice(0, 5).replace(':', '')}-${String(now.getMilliseconds()).padStart(3, '0')}`;
  }, [reportNumber]);

  const now = new Date();
  const formattedDate = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const formattedTime = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const authorName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.name || user?.username || 'Not specified';

  return (
    <div className="space-y-6 print-report">
      <div className="flex justify-end print:hidden">
        <button onClick={handlePrint} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rb-active text-white font-bold hover:bg-rb-surface transition-all shadow-md">
          <Download className="w-4 h-4 text-rb-brand" /> Download PDF Report
        </button>
      </div>

      {/* Report Header */}
      <div className="rb-card p-6 border border-slate-800/50">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-[10px] text-rb-brand font-bold uppercase tracking-widest mb-1">Wastewater Treatment Assessment</div>
            <h1 className="text-xl font-bold text-white">Reynolds & Bauhm</h1>
            <p className="text-sm text-slate-500 mt-1">Process Selection & Equipment Sizing Report</p>
          </div>
          <div className="text-right">
            <div className="inline-block px-4 py-2.5 rounded-xl bg-rb-brand/10 border border-rb-brand/20">
              <div className="text-[10px] text-rb-brand font-bold uppercase tracking-wider">Report Number</div>
              <div className="text-base font-bold text-rb-brand font-mono">{clientReportNum}</div>
            </div>
            <div className="text-xs text-slate-500 mt-2">Generated: {formattedDate} at {formattedTime}</div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-800/50 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
          <span>Classification: <strong className="text-white">{result.treatabilityClassification}</strong></span>
          <span>Treatability Index: <strong className="text-white">{result.overallTreatabilityIndex}</strong></span>
          <span>Biological Viability: <strong className="text-white">{result.biologicalViability.filter(v => v.passFail === 'PASS').length}/{result.biologicalViability.length}</strong></span>
          <span>Process Stages: <strong className="text-white">{sim?.stages.length ?? 0}</strong></span>
          <span>Author: <strong className="text-white">{authorName}</strong></span>
        </div>
      </div>

      {/* Input Summary */}
      <div className="rb-card overflow-hidden">
        <div className="p-4 border-b border-slate-800/50 bg-slate-800/30">
          <h3 className="text-sm font-bold text-white">Input Water Quality Summary</h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {[
              { label: 'Flow Rate', value: `${safeNum(blended?.params?.flowRate).toFixed(1)} m³/day` },
              { label: 'BOD₅', value: `${safeNum(blended?.params?.bod5).toFixed(0)} mg/L` },
              { label: 'COD', value: `${safeNum(blended?.params?.cod).toFixed(0)} mg/L` },
              { label: 'TSS', value: `${safeNum(blended?.params?.tss).toFixed(0)} mg/L` },
              { label: 'pH', value: safeNum(blended?.params?.ph).toFixed(2) },
              { label: 'TDS', value: `${safeNum(blended?.params?.tds).toFixed(0)} mg/L` },
              { label: 'TKN', value: `${safeNum(blended?.params?.tkn).toFixed(0)} mg/L` },
              { label: 'Total P', value: `${safeNum(blended?.params?.totalPhosphorus).toFixed(0)} mg/L` },
              { label: 'Conductivity', value: `${safeNum(blended?.params?.conductivity).toFixed(0)} µS/cm` },
              { label: 'O&G', value: `${safeNum(blended?.params?.oilGrease).toFixed(0)} mg/L` },
              { label: 'Temperature', value: `${safeNum(blended?.params?.temperature).toFixed(1)} °C` },
              { label: 'BOD/COD', value: (safeNum(blended?.params?.cod) > 0 ? safeNum(blended?.params?.bod5) / safeNum(blended?.params?.cod) : 0).toFixed(2) },
            ].map((item, i) => (
              <div key={i} className="p-2.5 rounded-lg bg-slate-800/50 border border-slate-800/50 text-center">
                <div className="text-[10px] text-slate-500 uppercase font-bold">{item.label}</div>
                <div className="text-sm font-bold text-white">{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Process Train Summary */}
      {sim && (
        <div className="rb-card overflow-hidden">
          <div className="p-4 border-b border-slate-800/50 bg-slate-800/30">
            <h3 className="text-sm font-bold text-white">Proposed Treatment Train ({sim.stages.length} Stages)</h3>
          </div>
          <div className="p-4 space-y-4">
            {sim.stages.map((stage, i) => {
              const isCompliant =
                stage.tss <= limits.tss && stage.bod5 <= limits.bod5 && stage.cod <= limits.cod &&
                stage.ph >= limits.phMin && stage.ph <= limits.phMax;
              return (
                <div key={i} className={`p-4 rounded-xl border ${isCompliant ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 text-white flex items-center justify-center font-bold text-sm">{stage.stageNumber}</div>
                      <div>
                        <div className="font-bold text-white text-sm">{stage.stageName}</div>
                        <div className="text-xs text-slate-500">{stage.equipment.map(e => e.name).join(' + ')}</div>
                      </div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isCompliant ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/15 text-red-400 border border-red-500/20'}`}>
                      {isCompliant ? 'COMPLIANT' : 'NON-COMPLIANT'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {[
                      { label: 'pH', value: safeNum(stage.ph).toFixed(2) },
                      { label: 'BOD', value: `${safeNum(stage.bod5).toFixed(0)} mg/L` },
                      { label: 'COD', value: `${safeNum(stage.cod).toFixed(0)} mg/L` },
                      { label: 'TSS', value: `${safeNum(stage.tss).toFixed(0)} mg/L` },
                      { label: 'BOD↓', value: `${(safeNum(stage.bodReductionSoFar) * 100).toFixed(1)}%` },
                      { label: 'Volume', value: `${stage.tanks.reduce((s, t) => s + safeNum(t.volumeM3), 0)} m³` },
                    ].map((p, pi) => (
                      <div key={pi} className="p-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-center">
                        <div className="text-[10px] text-slate-500 uppercase font-bold">{p.label}</div>
                        <div className="text-xs font-bold text-white">{p.value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                    {stage.tanks.map((tank, ti) => (
                      <div key={ti} className="p-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-xs">
                        <div className="font-semibold text-white">{tank.name}</div>
                        <div className="text-slate-500">{tank.volumeM3} m³ | {tank.hrt} hr HRT</div>
                        <div className="text-slate-600">{tank.dimensions}</div>
                      </div>
                    ))}
                  </div>
                  {stage.sludgeGeneration && (
                    <div className="mt-3 p-2.5 rounded-lg bg-rb-brand/5 border border-rb-brand/15 text-xs text-slate-400">
                      <span className="font-bold text-rb-brand">DAF Sludge:</span>{' '}
                      {safeNum(stage.sludgeGeneration?.volumeM3PerDay)} m³/day, {safeNum(stage.sludgeGeneration?.drySolidsKgPerDay).toLocaleString()} kg/day dry solids
                    </div>
                  )}
                  {stage.chemicals.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {stage.chemicals.map((chem, ci) => (
                        <span key={ci} className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] text-slate-400 font-medium">
                          {chem.name}: {chem.dose} {chem.unit}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Discharge Limits */}
      <div className="rb-card overflow-hidden">
        <div className="p-4 border-b border-slate-800/50 bg-slate-800/30">
          <h3 className="text-sm font-bold text-white">Discharge Limits Applied</h3>
        </div>
        <div className="p-4 grid grid-cols-3 md:grid-cols-6 gap-2">
          {[
            { label: 'SS', value: `${limits.tss} mg/L` },
            { label: 'BOD₅', value: `${limits.bod5} mg/L` },
            { label: 'COD', value: `${limits.cod} mg/L` },
            { label: 'O&G', value: `${limits.oilGrease} mg/L` },
            { label: 'TDS', value: `${limits.tds} mg/L` },
            { label: 'TKN', value: `${limits.tkn} mg/L` },
            { label: 'TN', value: `${limits.totalNitrogen} mg/L` },
            { label: 'TP', value: `${limits.totalPhosphorus} mg/L` },
            { label: 'pH', value: `${limits.phMin}-${limits.phMax}` },
            { label: 'Temp', value: `${limits.temperature} °C` },
            { label: 'Conductivity', value: `${limits.conductivity} µS/cm` },
          ].map((item, i) => (
            <div key={i} className="p-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-center">
              <div className="text-[10px] text-slate-500 uppercase font-bold">{item.label}</div>
              <div className="text-xs font-bold text-white">{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Sample History */}
      {hasSamplesChart && (
        <div className="rb-card overflow-hidden">
          <div className="p-4 border-b border-slate-800/50 bg-slate-800/30">
            <h3 className="text-sm font-bold text-white">Sample History ({samples.length} samples)</h3>
          </div>
          <div className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-800/60 border-b border-slate-700">
                    {['Date', 'Sample', 'BOD', 'COD', 'TSS', 'pH', 'TDS', 'TKN'].map(h => (
                      <th key={h} className="py-2 px-3 font-bold text-slate-400 uppercase text-[10px] tracking-wider text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedSamples.map((s, i) => (
                    <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                      <td className="py-2 px-3 text-slate-500">{new Date(s.date).toLocaleDateString('en-GB')}</td>
                      <td className="py-2 px-3 font-semibold text-white">{s.name}</td>
                      <td className="py-2 px-3 text-right font-mono text-cyan-400">{safeNum(s.params?.bod5).toFixed(0)}</td>
                      <td className="py-2 px-3 text-right font-mono text-rb-brand">{safeNum(s.params?.cod).toFixed(0)}</td>
                      <td className="py-2 px-3 text-right font-mono text-slate-400">{safeNum(s.params?.tss).toFixed(0)}</td>
                      <td className="py-2 px-3 text-right font-mono text-white">{safeNum(s.params?.ph).toFixed(2)}</td>
                      <td className="py-2 px-3 text-right font-mono text-slate-400">{safeNum(s.params?.tds).toFixed(0)}</td>
                      <td className="py-2 px-3 text-right font-mono text-slate-400">{safeNum(s.params?.tkn).toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="p-4 border-t border-slate-800/50 text-center text-xs text-slate-500">
        <div className="font-bold text-white text-sm">Reynolds & Bauhm</div>
        <div className="text-slate-500">Water Treatment Engineering</div>
        <div className="mt-2 text-[10px] text-slate-600 font-mono">
          Report: {clientReportNum} | Generated {formattedDate} {formattedTime} | Author: {authorName} | Confidential
        </div>
      </div>
    </div>
  );
}
